// src/belediye/pages/dashboard/DashboardV2Page.js

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
// FAZ-13: kapsam cutover'i
import usePermissions from "../../hooks/usePermissions";
// FAZ-17: sunum profili → görünür kart havuzu (yalnız görünüm; yetkiye etkisi yok)
import { visibleDashboardCards } from "./uiProfiles";
import { useDispatch, useSelector } from "react-redux";

import {
  dashboardV2SorguGetir,
  dashboardV2SessizSorguGetir,
  dashboardV2TarihAyarla,
  dashboardV2BirimAyarla,
  dashboardV2DurumlarAyarla,
  dashboardV2ExpandWidgetAyarla,
  dashboardV2WidgetDetayGetir,
  dashboardV2HataTemizle,
  selectDashboardV2Filtreler,
  selectDashboardV2Meta,
  selectDashboardV2UygulananFiltreler,
  selectDashboardV2Kpi,
  selectDashboardV2Grafikler,
  selectDashboardV2Harita,
  selectDashboardV2Kirilimlar,
  selectDashboardV2SonYeniBasvurular,
  selectDashboardV2CevapsizBasvurular,
  selectDashboardV2Yukleniyor,
  selectDashboardV2Hata,
  selectDashboardV2ExpandAcikWidget,
  selectDashboardV2WidgetDetaylari,
  selectDashboardV2WidgetYukleniyorByKey,
  selectDashboardV2WidgetHataByKey,
  selectDashboardV2SonGuncellemeEpochMs,
} from "../../redux/slices/belediyeDashboardV2Slice";

import DashboardExpandPanel from "./components/DashboardExpandPanel";
import DashboardLeafletMap from "./components/DashboardLeafletMap";

import {
  ArrowPathIcon,
  ArrowsPointingOutIcon,
  XMarkIcon,
  LockClosedIcon,
  CalendarDaysIcon,
  ClockIcon,
  ExclamationCircleIcon,
  InboxIcon,
  FunnelIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  Squares2X2Icon,
  BuildingOffice2Icon,
} from "@heroicons/react/24/outline";

import "./DashboardV2Page.css";

/* =============================================================================
   DEBUG
============================================================================= */
const isBrowser = typeof window !== "undefined";

const DEBUG =
  isBrowser &&
  (() => {
    try {
      return (
        window.localStorage.getItem("DEBUG_DASH_V2") === "1" ||
        window.__DEBUG_DASH_V2__ === true
      );
    } catch {
      return false;
    }
  })();

const log = (...args) => DEBUG && console.log("[DashV2]", ...args);

/* =============================================================================
   SABITLER
============================================================================= */
const TARIH_PRESETLER = [
  { value: "son7", label: "Son 7 Gün" },
  { value: "son30", label: "Son 30 Gün" },
  { value: "buAy", label: "Bu Ay" },
  { value: "buYil", label: "Bu Yıl" },
  { value: "ozel", label: "Özel Aralık..." },
];

const DURUMLAR = [
  { value: "tümü", label: "Tümü" },
  { value: "yeni", label: "Yeni" },
  { value: "işleme_alındı", label: "İncelemede" },
  { value: "çözüldü", label: "Çözüldü" },
  { value: "çözülemedi", label: "Çözülemedi" },
];

const WIDGET_TITLES = {
  latest_new: "Yeni Başvurular",
  waiting_new: "Bekleyen Başvurular",
  neighborhood_breakdown: "Mahalle Kırılımı",
  unit_breakdown: "Birim Kırılımı",
  open_age_distribution: "Açık Başvuru Süre Dağılımı",
};

/* =============================================================================
   HELPERS
============================================================================= */
const ymd = (d) => {
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

const gunEkle = (d, g) => {
  const x = new Date(d);
  x.setDate(x.getDate() + g);
  return x;
};

const preseteTarihCevir = (preset) => {
  const b = new Date();

  switch (preset) {
    case "son7":
      return { date_from: ymd(gunEkle(b, -6)), date_to: ymd(b) };
    case "son30":
      return { date_from: ymd(gunEkle(b, -29)), date_to: ymd(b) };
    case "buAy":
      return {
        date_from: ymd(new Date(b.getFullYear(), b.getMonth(), 1)),
        date_to: ymd(b),
      };
    case "buYil":
      return {
        date_from: ymd(new Date(b.getFullYear(), 0, 1)),
        date_to: ymd(b),
      };
    default:
      return null;
  }
};

const sayiFormat = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  return Number.isFinite(n)
    ? new Intl.NumberFormat("tr-TR").format(n)
    : "—";
};

const yuzdeFormat = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  return Number.isFinite(n)
    ? `%${new Intl.NumberFormat("tr-TR", {
        maximumFractionDigits: 0,
      }).format(n)}`
    : "—";
};

const saatFormat = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  return Number.isFinite(n)
    ? `${new Intl.NumberFormat("tr-TR", {
        maximumFractionDigits: 1,
      }).format(n)} sa`
    : "—";
};

const beklemeFormat = (s) => {
  if (s === null || s === undefined) return "—";

  const n = Number(s);
  if (!Number.isFinite(n)) return "—";

  if (n < 24) return `${n}s`;

  const gun = Math.floor(n / 24);
  const kalan = n % 24;

  return kalan > 0 ? `${gun}g ${kalan}s` : `${gun}g`;
};

const trTarihSaat = (iso) => {
  if (!iso) return "—";

  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const trStatusEtiket = (d) => {
  const map = {
    yeni: "Yeni",
    işleme_alındı: "İncelemede",
    çözüldü: "Çözüldü",
    çözülemedi: "Çözülemedi",
    tümü: "Tümü",
  };
  return map[d] || String(d || "");
};

const temizStatusListesi = (statuses) => {
  if (!Array.isArray(statuses)) return ["yeni", "işleme_alındı"];
  if (statuses.length === 0 || statuses.includes("tümü")) return ["tümü"];
  return statuses;
};



/* =============================================================================
   HOOKS
============================================================================= */
function useCountUp(target, duration = 800) {
  const [current, setCurrent] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const startValRef = useRef(0);

  useEffect(() => {
    if (
      target === null ||
      target === undefined ||
      !Number.isFinite(Number(target))
    ) {
      setCurrent(target);
      return undefined;
    }

    const end = Number(target);
    const start = startValRef.current;
    startRef.current = null;

    const animate = (ts) => {
      if (!startRef.current) startRef.current = ts;

      const progress = Math.min((ts - startRef.current) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);

      setCurrent(Math.round(start + (end - start) * ease));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        startValRef.current = end;
      }
    };

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration]);

  return current;
}

function useClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler(e);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

/* =============================================================================
   SMALL COMPONENTS
============================================================================= */
function KPICard({
  label,
  value,
  sub,
  icon: Icon,
  accentClass = "",
  isLoading,
}) {
  const isNum = typeof value === "number" && Number.isFinite(value);
  const animatedVal = useCountUp(isNum ? value : null);

  return (
    <div className={`dash-kpi-card ${accentClass}`}>
      <div className="dash-kpi-head">
        <span className="dash-kpi-label">{label}</span>
        <div className="dash-kpi-icon">{Icon && <Icon />}</div>
      </div>

      {isLoading ? (
        <div
          className="skel-block"
          style={{ height: 24, width: "54%", borderRadius: 6, margin: "2px 0" }}
        />
      ) : (
        <div className="dash-kpi-value">
          {isNum
            ? new Intl.NumberFormat("tr-TR").format(animatedVal)
            : value || "—"}
        </div>
      )}

      <div className="dash-kpi-sub">{sub || "\u00A0"}</div>
    </div>
  );
}

function KPISkeleton() {
  return (
    <div className="dash-kpi-card">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <div
          className="skel-block"
          style={{ height: 9, width: "48%", borderRadius: 4 }}
        />
        <div
          className="skel-block"
          style={{ height: 24, width: 24, borderRadius: 8 }}
        />
      </div>
      <div
        className="skel-block"
        style={{ height: 24, width: "44%", borderRadius: 6, marginBottom: 8 }}
      />
      <div
        className="skel-block"
        style={{ height: 9, width: "72%", borderRadius: 4 }}
      />
    </div>
  );
}

function CardSkeleton({ height = 200 }) {
  return (
    <div className="dash-card">
      <div className="dash-card-header">
        <div
          className="skel-block"
          style={{ height: 12, width: 110, borderRadius: 4 }}
        />
      </div>
      <div className="dash-card-body">
        <div className="skel-block" style={{ height, borderRadius: 10 }} />
      </div>
    </div>
  );
}

function IconExpandButton({ onClick, title }) {
  return (
    <button
      className="dash-card-expand-btn"
      onClick={onClick}
      type="button"
      title={title}
      aria-label={title}
      style={{ padding: 0, width: 30, justifyContent: "center" }}
    >
      <ArrowsPointingOutIcon />
    </button>
  );
}

function DashCard({
  title,
  subtitle,
  children,
  onExpand,
  isLoading,
  className = "",
  bodyClassName = "",
  headerRight = null,
  style = undefined,
  expandIconOnly = false,
  expandTitle = "Büyüt",
}) {
  return (
    <div
      className={`dash-card ${isLoading ? "dash-updating" : ""} ${className}`}
      style={style}
    >
      <div className="dash-card-header">
        <div className="dash-card-header-left">
          <div className="dash-card-title">{title}</div>
          {subtitle && <div className="dash-card-subtitle">{subtitle}</div>}
        </div>

        {headerRight ? (
          headerRight
        ) : onExpand ? (
          <button
            className="dash-card-expand-btn"
            onClick={onExpand}
            type="button"
            title={expandTitle}
            aria-label={expandTitle}
            style={
              expandIconOnly
                ? { padding: 0, width: 30, justifyContent: "center" }
                : undefined
            }
          >
            <ArrowsPointingOutIcon />
          </button>
        ) : null}
      </div>

      <div className={`dash-card-body ${bodyClassName}`.trim()}>{children}</div>
    </div>
  );
}

function InlineBarChart({
  rows = [],
  labelKey = "etiket",
  valueKey = "sayi",
  secondaryRenderer,
  color,
  limit = 5,
}) {
  const limitedRows = rows.slice(0, limit);
  const max = Math.max(1, ...limitedRows.map((r) => Number(r?.[valueKey] || 0)));

  if (!limitedRows.length) {
    return (
      <div className="dash-empty-lite">
        <InboxIcon style={{ width: 18, height: 18 }} />
        <span>Veri yok</span>
      </div>
    );
  }

  return (
    <div className="dash-mini-bars">
      {limitedRows.map((row, i) => {
        const val = Number(row?.[valueKey] || 0);
        const pct = max > 0 ? (val / max) * 100 : 0;

        return (
          <div className="dash-mini-bar-row" key={i}>
            <div className="dash-mini-bar-head">
              <span className="dash-mini-bar-label" title={row?.[labelKey]}>
                {row?.[labelKey]}
              </span>

              <div className="dash-mini-bar-values">
                {secondaryRenderer && (
                  <span className="dash-mini-bar-secondary">
                    {secondaryRenderer(row)}
                  </span>
                )}
                <strong>{sayiFormat(val)}</strong>
              </div>
            </div>

            <div className="dash-mini-bar-track">
              <div
                className="dash-mini-bar-fill"
                style={{
                  width: `${pct}%`,
                  background: color || undefined,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DonutChart({ acik = 0, kapali = 0 }) {
  const toplam = acik + kapali;

  if (!toplam) {
    return (
      <div className="dash-empty-lite">
        <InboxIcon style={{ width: 18, height: 18 }} />
        <span>Veri yok</span>
      </div>
    );
  }

  const R = 38;
  const cx = 52;
  const cy = 52;
  const C = 2 * Math.PI * R;
  const kapaliDash = (kapali / toplam) * C;
  const acikDash = C - kapaliDash;
  const kapaliPct = (kapali / toplam) * 100;

  return (
    <div className="dash-donut-wrap">
      <svg width="104" height="104" viewBox="0 0 104 104">
        <circle
          cx={cx}
          cy={cy}
          r={R}
          fill="none"
          stroke="var(--color-bg-soft)"
          strokeWidth="13"
        />

        {kapali > 0 && (
          <circle
            cx={cx}
            cy={cy}
            r={R}
            fill="none"
            stroke="var(--color-success)"
            strokeWidth="13"
            strokeDasharray={`${kapaliDash} ${C}`}
            strokeLinecap="round"
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        )}

        {acik > 0 && (
          <circle
            cx={cx}
            cy={cy}
            r={R}
            fill="none"
            stroke="var(--color-warning)"
            strokeWidth="13"
            strokeDasharray={`${acikDash} ${C}`}
            strokeLinecap="round"
            transform={`rotate(${-90 + (kapali / toplam) * 360} ${cx} ${cy})`}
          />
        )}

        <text
          x={cx}
          y={cy - 4}
          textAnchor="middle"
          fontSize="13"
          fontWeight="800"
          fill="var(--color-text)"
        >
          {sayiFormat(toplam)}
        </text>
        <text
          x={cx}
          y={cy + 10}
          textAnchor="middle"
          fontSize="9"
          fill="var(--color-text-muted)"
          fontWeight="600"
        >
          Toplam
        </text>
      </svg>

      <div className="dash-donut-legend">
        <div className="dash-donut-legend-item">
          <div
            className="dash-donut-legend-dot"
            style={{ background: "var(--color-warning)" }}
          />
          <span className="dash-donut-legend-label">Açık</span>
          <span className="dash-donut-legend-val">{sayiFormat(acik)}</span>
        </div>

        <div className="dash-donut-legend-item">
          <div
            className="dash-donut-legend-dot"
            style={{ background: "var(--color-success)" }}
          />
          <span className="dash-donut-legend-label">Kapalı</span>
          <span className="dash-donut-legend-val">{sayiFormat(kapali)}</span>
        </div>

        <div className="dash-donut-legend-item">
          <div
            className="dash-donut-legend-dot"
            style={{ background: "transparent" }}
          />
          <span className="dash-donut-legend-label">Kapalı Oranı</span>
          <span
            className="dash-donut-legend-val"
            style={{ color: "var(--color-success)" }}
          >
            {yuzdeFormat(kapaliPct)}
          </span>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ durum }) {
  const cls =
    {
      yeni: "dash-status-badge--yeni",
      işleme_alındı: "dash-status-badge--isleme",
      çözüldü: "dash-status-badge--cozuldu",
      çözülemedi: "dash-status-badge--cozulemedi",
    }[durum] || "dash-status-badge--default";

  return (
    <span className={`dash-status-badge ${cls}`}>
      {trStatusEtiket(durum)}
    </span>
  );
}

function QueueCard({
  newRows = [],
  waitingRows = [],
  onExpandNew,
  onExpandWaiting,
  style = undefined,
}) {
  const [tab, setTab] = useState("new");
  const rows = tab === "new" ? newRows : waitingRows;

  return (
    <div className="dash-card dash-card--queue" style={style}>
      <div className="dash-card-header">
        <div className="dash-queue-tabs">
          <button
            className={`dash-queue-tab${tab === "new" ? " active" : ""}`}
            onClick={() => setTab("new")}
            type="button"
          >
            <CheckCircleIcon />
            <span>Yeni</span>
          </button>

          <button
            className={`dash-queue-tab${tab === "waiting" ? " active" : ""}`}
            onClick={() => setTab("waiting")}
            type="button"
          >
            <ClockIcon />
            <span>Bekleyenler</span>
          </button>
        </div>

        <div style={{ flex: 1 }} />

        <IconExpandButton
          onClick={tab === "new" ? onExpandNew : onExpandWaiting}
          title={tab === "new" ? "Yeni başvuruları büyüt" : "Bekleyen başvuruları büyüt"}
        />
      </div>

      <div className="dash-card-body dash-card-body--compact-list">
        {!rows.length ? (
          <div className="dash-empty-lite">
            <InboxIcon style={{ width: 18, height: 18 }} />
            <span>
              {tab === "new"
                ? "Kapsamınızda yeni başvuru yok"
                : "Bekleyen (cevapsız) başvuru yok"}
            </span>
          </div>
        ) : (
          <div className="dash-queue-list dash-queue-list--compact">
            {rows.slice(0, 5).map((row, i) => (
              <div
                key={row?.id || i}
                className="dash-queue-item dash-queue-item--compact"
                style={{ minHeight: 0 }}
              >
                <div className="dash-queue-item-head">
                  <div
                    className="dash-queue-item-title"
                    style={{
                      whiteSpace: "normal",
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {row?.baslik || "Başlıksız başvuru"}
                  </div>
                  <StatusBadge durum={row?.durum} />
                </div>

                <div
                  className="dash-queue-item-meta"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: "4px 10px",
                  }}
                >
                  <span title={row?.takipNo || "—"}>{row?.takipNo || "—"}</span>
                  <span title={row?.birimAdi || "—"}>{row?.birimAdi || "—"}</span>
                  <span title={row?.mahalleAdi || "Belirtilmemiş"}>
                    {row?.mahalleAdi || "Belirtilmemiş"}
                  </span>
                  <span>{trTarihSaat(row?.tarih)}</span>
                </div>

                {tab === "waiting" && (
                  <div className="dash-queue-item-foot">
                    <span>Bekleme</span>
                    <strong style={{ color: "var(--color-warning)" }}>
                      {row?.beklemeEtiketi || beklemeFormat(row?.acikSaat)}
                    </strong>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusDropdown({ statuses, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useClickOutside(ref, () => setOpen(false));

  const clean = temizStatusListesi(statuses);
  const tumuMu = clean.includes("tümü");

  const label = useMemo(() => {
    if (tumuMu) return "Durum: Tümü";
    if (clean.length === 1) return `Durum: ${trStatusEtiket(clean[0])}`;
    if (clean.length === 2) {
      return `Durum: ${trStatusEtiket(clean[0])} + ${trStatusEtiket(clean[1])}`;
    }
    return `Durum: ${clean.length} seçim`;
  }, [clean, tumuMu]);

  const toggle = (val) => {
    if (val === "tümü") {
      onChange(["tümü"]);
      return;
    }

    const base = tumuMu ? [] : [...clean];
    const exists = base.includes(val);
    const next = exists ? base.filter((x) => x !== val) : [...base, val];

    onChange(next.length ? next : ["tümü"]);
  };

  return (
    <div className="dash-filter-item" ref={ref}>
      <button
        className="dash-filter-select dash-filter-select--button"
        onClick={() => setOpen((prev) => !prev)}
        type="button"
      >
        <span>{label}</span>
        <ChevronDownIcon style={{ width: 14, height: 14, flexShrink: 0 }} />
      </button>

      {open && (
        <div className="dash-status-dropdown pop-ok" data-ok="ust" data-ok-x="sol">
          {DURUMLAR.map((d) => {
            const aktif =
              d.value === "tümü"
                ? tumuMu
                : !tumuMu && clean.includes(d.value);

            return (
              <label
                key={d.value}
                className={`dash-status-option${aktif ? " active" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={aktif}
                  onChange={() => toggle(d.value)}
                />
                <span>{d.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SingleSelectDropdown({
  label,
  value,
  options = [],
  onChange,
  placeholder = "Seçiniz",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useClickOutside(ref, () => setOpen(false));

  const selected = options.find((opt) => String(opt.value) === String(value));
  const text = selected?.label || placeholder;

  return (
    <div className="dash-filter-item" ref={ref}>
      <button
        className="dash-filter-select dash-filter-select--button"
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
        type="button"
        disabled={disabled}
      >
        <span>{label ? `${label}: ${text}` : text}</span>
        <ChevronDownIcon style={{ width: 14, height: 14, flexShrink: 0 }} />
      </button>

      {open && !disabled && (
        <div className="dash-status-dropdown pop-ok" data-ok="ust" data-ok-x="sol">
          {options.map((opt) => {
            const aktif = String(opt.value) === String(value);

            return (
              <button
                key={String(opt.value)}
                type="button"
                className={`dash-status-option${aktif ? " active" : ""}`}
                onClick={() => {
                  onChange?.(opt.value);
                  setOpen(false);
                }}
                style={{
                  width: "100%",
                  border: "none",
                  background: "transparent",
                  textAlign: "left",
                }}
              >
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BreakdownTabbedCard({
  mahalleRows = [],
  birimRows = [],
  onExpandNeighborhood,
  onExpandUnit,
  isLoading,
  isBaskan = true,
  style = undefined,
}) {
  const [activeTab, setActiveTab] = useState("mahalle");

  // "Birim" kırılımı yalnız kapsamı tüm birimleri kapsayan (all_units / başkan)
  // kullanıcıya anlamlı — backend birim kırılımını yalnız onlara üretir
  // (tables.py birimlerIlk5). Değilse sekme gizlenir; kalıcı boş "Birim" tab'ı olmaz.
  const isMahalle = !isBaskan || activeTab === "mahalle";

  const rows = isMahalle
    ? mahalleRows.map((m) => ({
        etiket: m?.mahalleAdi,
        sayi: m?.toplam,
        oran: m?.payOraniYuzde,
      }))
    : birimRows.map((b) => ({
        etiket: b?.birimAdi,
        sayi: b?.toplam,
        oran: b?.payOraniYuzde,
      }));

  const headerRight = (
    <div className="dash-tabbed-card-actions">
      <div
        className="dash-segmented-tabs"
        role="tablist"
        aria-label="Kırılım sekmeleri"
      >
        <button
          type="button"
          className={`dash-segmented-tab${isMahalle ? " active" : ""}`}
          onClick={() => setActiveTab("mahalle")}
        >
          <Squares2X2Icon />
          <span>Mahalle</span>
        </button>

        {isBaskan && (
          <button
            type="button"
            className={`dash-segmented-tab${!isMahalle ? " active" : ""}`}
            onClick={() => setActiveTab("birim")}
          >
            <BuildingOffice2Icon />
            <span>Birim</span>
          </button>
        )}
      </div>

      <IconExpandButton
        onClick={isMahalle ? onExpandNeighborhood : onExpandUnit}
        title={isMahalle ? "Mahalle kırılımını büyüt" : "Birim kırılımını büyüt"}
      />
    </div>
  );

  return (
    <DashCard
      title={isBaskan ? "Mahalle / Birim Kırılımı" : "Mahalle Kırılımı"}
      subtitle={isMahalle ? "İlk 5 mahalle" : "İlk 5 birim"}
      isLoading={isLoading}
      className="dash-card--breakdown-tabs"
      bodyClassName="dash-card-body--compact-list"
      headerRight={headerRight}
      style={style}
    >
      <InlineBarChart
        rows={rows}
        labelKey="etiket"
        valueKey="sayi"
        secondaryRenderer={(row) =>
          row?.oran != null ? yuzdeFormat(row.oran) : null
        }
        color={isMahalle ? "var(--color-primary-soft)" : undefined}
        limit={5}
      />
    </DashCard>
  );
}

/* =============================================================================
   PAGE
============================================================================= */
export default function DashboardV2Page() {
  // FAZ-13: kapsam cutover'ı (uiOn=false iken eski rol yolu birebir)
  const { uiOn, scope: authzScope, uiProfile, canAll } = usePermissions();
  const dispatch = useDispatch();

  const filtreler = useSelector(selectDashboardV2Filtreler);
  const meta = useSelector(selectDashboardV2Meta);
  const uygulananFiltreler = useSelector(selectDashboardV2UygulananFiltreler);
  const kpi = useSelector(selectDashboardV2Kpi);
  const grafikler = useSelector(selectDashboardV2Grafikler);
  const harita = useSelector(selectDashboardV2Harita);
  const kirilimlar = useSelector(selectDashboardV2Kirilimlar);
  const sonYeni = useSelector(selectDashboardV2SonYeniBasvurular);
  const cevapsiz = useSelector(selectDashboardV2CevapsizBasvurular);
  const yukleniyor = useSelector(selectDashboardV2Yukleniyor);
  const hata = useSelector(selectDashboardV2Hata);
  const expandAcikWidget = useSelector(selectDashboardV2ExpandAcikWidget);
  const widgetDetaylari = useSelector(selectDashboardV2WidgetDetaylari);
  const widgetYukleniyor = useSelector(selectDashboardV2WidgetYukleniyorByKey);
  const widgetHata = useSelector(selectDashboardV2WidgetHataByKey);
  const sonGuncellemeEpochMs = useSelector(selectDashboardV2SonGuncellemeEpochMs);

  const initialFetchDoneRef = useRef(false);

  const [tarihPreset, setTarihPreset] = useState("son30");
  const [ozelTarih, setOzelTarih] = useState({ from: "", to: "" });
  const [toastAcik, setToastAcik] = useState(false);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(
    isBrowser ? window.innerWidth : 1440
  );

  const kullanici = useMemo(() => {
    try {
      const raw = isBrowser ? window.localStorage.getItem("user") : null;
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!isBrowser) return undefined;

    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);

    return () => window.removeEventListener("resize", onResize);
  }, []);

  // FAZ-13 (3.2): birincil kaynak backend meta.baskanMi (degismedi);
  // fallback uiOn'da KAPSAM (scope.mode), uiOn=false'ta eski rol string'i.
  const isBaskan = meta?.baskanMi ?? (uiOn ? authzScope?.mode === "all_units" : kullanici?.role === "başkan");

  // FAZ-17 — sunum profili → görünür kart kümesi.
  //  • uiOn=false (eski yol): süzgeç YOK → null → tüm kartlar (bugünkü DOM birebir).
  //  • uiOn=true: profil (operasyonel/yonetici) + kart izin süzgeci (canAll).
  // Yalnız GÖRÜNÜM/dizilim etkiler; yetki motoruna dokunmaz (başkan≠superuser).
  const gorunurKartlar = useMemo(() => {
    if (!uiOn) return null; // legacy: tüm kartlar görünür
    return new Set(visibleDashboardCards(uiProfile, canAll));
  }, [uiOn, uiProfile, canAll]);
  const kartGorunur = (key) => gorunurKartlar === null || gorunurKartlar.has(key);
  const isTabletLayout = viewportWidth <= 1360;
  const mapHeight = 388;

  const adSoyad = useMemo(() => {
    if (!kullanici) return "Kullanıcı";

    return (
      `${kullanici.first_name || ""} ${kullanici.last_name || ""}`.trim() ||
      kullanici.username ||
      "Kullanıcı"
    );
  }, [kullanici]);

const birimler = useMemo(() => {
  // Yeni kanonik kaynak:
  // backend dashboard response içindeki tam birim seçenekleri
  const fromFilterOptions = Array.isArray(meta?.filterOptions?.units)
    ? meta.filterOptions.units
    : [];

  // Geçiş uyumu için alternatif alan desteği
  const fromMetaUnits = Array.isArray(meta?.birimSecenekleri)
    ? meta.birimSecenekleri
    : [];

  const raw = fromFilterOptions.length ? fromFilterOptions : fromMetaUnits;

  return raw
    .map((u) => ({
      id: Number(u?.id),
      name: String(u?.name || "").trim(),
      slug: String(u?.slug || "").trim(),
    }))
    .filter((u) => Number.isFinite(u.id) && u.name)
    .sort((a, b) => a.name.localeCompare(b.name, "tr"));
}, [meta]);

  const aktifBirimAdi = useMemo(() => {
    const id = Number(meta?.aktifBirimId ?? filtreler?.unit_id);

    if (!Number.isFinite(id)) {
      return isBaskan ? "Tüm Birimler" : "Aktif birim";
    }

    const found = birimler.find((b) => Number(b.id) === id);
    return found?.name || `Birim #${id}`;
  }, [birimler, meta, filtreler, isBaskan]);

  const widgetStatuses = useMemo(
    () => temizStatusListesi(filtreler?.statuses),
    [filtreler?.statuses]
  );

  const widgetStatusesSignature = useMemo(
    () => JSON.stringify(widgetStatuses),
    [widgetStatuses]
  );

  const mapPointCount = Array.isArray(harita?.noktalar) ? harita.noktalar.length : 0;
  const appliedRangeLabel = meta?.seciliAralikBasligi || "Seçili aralık";
  const isFirstLoad = yukleniyor && !sonGuncellemeEpochMs;

  useEffect(() => {
    if (sonGuncellemeEpochMs) {
      initialFetchDoneRef.current = true;
      return;
    }

    if (initialFetchDoneRef.current) return;
    if (yukleniyor) return;

    initialFetchDoneRef.current = true;
    dispatch(dashboardV2SorguGetir());
  }, [dispatch, yukleniyor, sonGuncellemeEpochMs]);

  // FAZ-65: 5 DAKİKALIK SESSİZ YENİLEME KALDIRILDI (kullanıcı kararı
  // 2026-07-28). Dashboard'ın kendi "Yenile" düğmesi ve "son güncelleme"
  // damgası var — panel ne zaman tazelendiğini SÖYLÜYOR; kullanıcı istediğinde
  // tazeliyor. Kendiliğinden yenilenen bir tablo, okurken altınızdan sayı
  // değiştiriyor.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onVis = () => {
      if (document.visibilityState !== "visible" || yukleniyor) return;
      dispatch(dashboardV2SessizSorguGetir());
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [dispatch, yukleniyor]);

  useEffect(() => {
    if (!hata) {
      setToastAcik(false);
      return undefined;
    }

    setToastAcik(true);

    const t = setTimeout(() => setToastAcik(false), 5000);
    return () => clearTimeout(t);
  }, [hata]);

  useEffect(() => {
    const from = uygulananFiltreler?.tarihBaslangic;
    const to = uygulananFiltreler?.tarihBitis;
    if (!from || !to) return;

    const { bugun, ayBaslangic, yilBaslangic } = uygulananFiltreler;

    if (from === yilBaslangic && to === bugun) {
      setTarihPreset("buYil");
      return;
    }

    if (from === ayBaslangic && to === bugun) {
      setTarihPreset("buAy");
      return;
    }

    const s30 = preseteTarihCevir("son30");
    const s7 = preseteTarihCevir("son7");

    if (from === s30?.date_from && to === s30?.date_to) {
      setTarihPreset("son30");
      return;
    }

    if (from === s7?.date_from && to === s7?.date_to) {
      setTarihPreset("son7");
      return;
    }

    setTarihPreset("ozel");
    setOzelTarih({ from, to });
  }, [uygulananFiltreler]);

  useEffect(() => {
    if (!expandAcikWidget) return;

    dispatch(
      dashboardV2WidgetDetayGetir({
        widget: expandAcikWidget,
        limit: 50,
        offset: 0,
      })
    );
  }, [
    dispatch,
    expandAcikWidget,
    filtreler?.date_from,
    filtreler?.date_to,
    filtreler?.unit_id,
    widgetStatusesSignature,
  ]);

  const tarihPresetDegistir = useCallback(
    (preset) => {
      setTarihPreset(preset);

      if (preset === "ozel") return;

      const aralik = preseteTarihCevir(preset);
      if (!aralik) return;

      dispatch(dashboardV2TarihAyarla(aralik));
      dispatch(dashboardV2SorguGetir(aralik));
    },
    [dispatch]
  );

  const ozelTarihUygula = useCallback(() => {
    if (!ozelTarih.from || !ozelTarih.to) return;

    const payload = {
      date_from: ozelTarih.from,
      date_to: ozelTarih.to,
    };

    dispatch(dashboardV2TarihAyarla(payload));
    dispatch(dashboardV2SorguGetir(payload));
  }, [dispatch, ozelTarih]);

  const birimDegistir = useCallback(
    (val) => {
      const next = val === "" ? undefined : Number(val);
      dispatch(dashboardV2BirimAyarla(next));
      dispatch(dashboardV2SorguGetir({ unit_id: next }));
    },
    [dispatch]
  );

  const durumDegistir = useCallback(
    (next) => {
      const final = temizStatusListesi(next);
      dispatch(dashboardV2DurumlarAyarla(final));
      dispatch(dashboardV2SorguGetir({ statuses: final }));
    },
    [dispatch]
  );

  const yenile = useCallback(() => {
    if (yukleniyor) return;
    dispatch(dashboardV2SorguGetir());
  }, [dispatch, yukleniyor]);

  const openExpand = useCallback(
    (key) => {
      dispatch(dashboardV2ExpandWidgetAyarla(key));
    },
    [dispatch]
  );

  const closeExpand = useCallback(() => {
    dispatch(dashboardV2ExpandWidgetAyarla(null));
  }, [dispatch]);

  const retryExpand = useCallback(() => {
    if (!expandAcikWidget) return;

    dispatch(
      dashboardV2WidgetDetayGetir({
        widget: expandAcikWidget,
        limit: 50,
        offset: 0,
      })
    );
  }, [dispatch, expandAcikWidget]);

  const statuses = widgetStatuses;

  const durumRows = useMemo(
    () =>
      (grafikler?.durumDagilimi || []).map((x) => ({
        etiket: x?.etiket || trStatusEtiket(x?.durum),
        sayi: x?.sayi || 0,
        oran: x?.oran,
      })),
    [grafikler]
  );

  const mahalleRows = useMemo(() => {
    const rows = kirilimlar?.mahallelerIlk5 || kirilimlar?.mahallelerIlk10 || [];
    return rows.slice(0, 5);
  }, [kirilimlar]);

  const birimRows = useMemo(() => {
    const rows = kirilimlar?.birimlerIlk5 || kirilimlar?.birimler || [];
    return rows.slice(0, 5);
  }, [kirilimlar]);

  const acikKapali = useMemo(
    () => grafikler?.acikKapali || { acik: 0, kapali: 0 },
    [grafikler]
  );

  const ageChartRows = useMemo(
    () =>
      (grafikler?.acikIsYasiDagilimi || []).map((x) => ({
        etiket: x?.aralik,
        sayi: x?.sayi,
      })),
    [grafikler]
  );

  const ageSummaryRows = useMemo(
    () =>
      (grafikler?.acikIsYasiDagilimi || []).map((x) => ({
        aralik: x?.aralik,
        sayi: x?.sayi,
      })),
    [grafikler]
  );

  const curEnvelope = expandAcikWidget
    ? widgetDetaylari?.[expandAcikWidget] || null
    : null;

  const curLoading = expandAcikWidget
    ? Boolean(widgetYukleniyor?.[expandAcikWidget])
    : false;

  const curError = expandAcikWidget
    ? widgetHata?.[expandAcikWidget] || null
    : false;

  const summaryForWidget = useMemo(
    () => ({
      latest_new: sonYeni || [],
      waiting_new: cevapsiz || [],
      neighborhood_breakdown: mahalleRows,
      unit_breakdown: birimRows,
      open_age_distribution: ageSummaryRows,
    }),
    [sonYeni, cevapsiz, mahalleRows, birimRows, ageSummaryRows]
  );

  const mainTopGridStyle = useMemo(() => {
    if (isTabletLayout) {
      return { gridTemplateColumns: "1fr", alignItems: "start" };
    }

    return {
      gridTemplateColumns: "minmax(0, 1.02fr) minmax(380px, 0.98fr)",
      alignItems: "start",
    };
  }, [isTabletLayout]);

  const mapCardStyle = useMemo(
    () => ({
      marginTop: !isTabletLayout ? 20 : 0,
      alignSelf: "start",
    }),
    [isTabletLayout]
  );

  // Tam ekran harita: ESC ile kapat + arka planı kilitle
  useEffect(() => {
    if (!mapFullscreen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setMapFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [mapFullscreen]);

  const bottomGridStyle = useMemo(() => {
    if (isTabletLayout) {
      return { gridTemplateColumns: "1fr", alignItems: "stretch" };
    }

    return {
      gridTemplateColumns: "minmax(300px, 0.64fr) minmax(0, 1.36fr)",
      alignItems: "stretch",
    };
  }, [isTabletLayout]);

  log("render", { filtreler, meta, mapFullscreen });

  return (
    <div className={`dash-page${mapFullscreen ? " dash-page--mapfs" : ""}`}>
      {toastAcik && hata && (
        <div className="dash-toast">
          <ExclamationCircleIcon
            style={{ width: 16, height: 16, flexShrink: 0 }}
          />
          <span>{hata?.mesaj || "Bir hata oluştu."}</span>

          <button
            className="dash-toast-close"
            type="button"
            onClick={() => {
              dispatch(dashboardV2HataTemizle());
              setToastAcik(false);
            }}
          >
            <XMarkIcon style={{ width: 14, height: 14 }} />
          </button>
        </div>
      )}

      <header className="dash-header">
        <div className="dash-toolbar-card">
          <div className="dash-greeting">
            <h1 className="dash-greeting-title">Hoş geldiniz, {adSoyad}</h1>
          </div>

          <div className="dash-filterbar">
            {isBaskan ? (
              <SingleSelectDropdown
  label="Birim"
  value={filtreler?.unit_id ?? ""}
  onChange={birimDegistir}
  disabled={!birimler.length}
  placeholder={birimler.length ? "Tüm Birimler" : "Yükleniyor..."}
  options={[
    { value: "", label: "Tüm Birimler" },
    ...birimler.map((b) => ({
      value: b.id,
      label: b.name,
    })),
  ]}
/>
            ) : (
              <div className="dash-filter-pill">
                <LockClosedIcon />
                <span>{aktifBirimAdi}</span>
              </div>
            )}

            <SingleSelectDropdown
  label="Tarih"
  value={tarihPreset}
  onChange={tarihPresetDegistir}
  options={TARIH_PRESETLER.map((p) => ({
    value: p.value,
    label: p.label,
  }))}
/>

            {tarihPreset === "ozel" && (
              <div className="dash-filter-item dash-filter-item--range">
                <input
                  type="date"
                  className="dash-date-input"
                  value={ozelTarih.from}
                  max={ozelTarih.to || undefined}
                  onChange={(e) =>
                    setOzelTarih((prev) => ({ ...prev, from: e.target.value }))
                  }
                />

                <span className="dash-range-sep">–</span>

                <input
                  type="date"
                  className="dash-date-input"
                  value={ozelTarih.to}
                  min={ozelTarih.from || undefined}
                  onChange={(e) =>
                    setOzelTarih((prev) => ({ ...prev, to: e.target.value }))
                  }
                />

                <button
                  className="dash-inline-btn"
                  onClick={ozelTarihUygula}
                  type="button"
                >
                  Uygula
                </button>
              </div>
            )}

            <StatusDropdown statuses={statuses} onChange={durumDegistir} />
 
            <div className="dash-filter-actions">
              <button
                className="dash-inline-btn"
                onClick={yenile}
                disabled={yukleniyor}
                type="button"
              >
                <ArrowPathIcon className={yukleniyor ? "is-spinning" : ""} />
                <span>Yenile</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="dash-body">
        {kartGorunur("kpi_ozet") && (
        <section className="dash-kpi-grid">
          {isFirstLoad && !kpi ? (
            Array.from({ length: 7 }).map((_, i) => <KPISkeleton key={i} />)
          ) : (
            <>
              <KPICard
                label="Bugün"
                value={kpi?.bugunBasvuruSayisi}
                sub="Bugün gelen"
                icon={CalendarDaysIcon}
                accentClass="dash-kpi-card--primary"
                isLoading={isFirstLoad && !kpi}
              />
              <KPICard
                label="Bu Ay"
                value={kpi?.buAyBasvuruSayisi}
                sub="Ay başından bugüne"
                icon={CalendarDaysIcon}
                accentClass="dash-kpi-card--blue"
                isLoading={isFirstLoad && !kpi}
              />
              <KPICard
                label="Bu Yıl"
                value={kpi?.buYilBasvuruSayisi}
                sub="Yıl başından bugüne"
                icon={ChartBarIcon}
                accentClass="dash-kpi-card--purple"
                isLoading={isFirstLoad && !kpi}
              />
              <KPICard
                label="Tüm Zamanlar"
                value={kpi?.tumZamanlarBasvuruSayisi}
                sub="Kapsam toplamı"
                icon={DocumentTextIcon}
                accentClass="dash-kpi-card--dark"
                isLoading={isFirstLoad && !kpi}
              />
              <KPICard
                label="Seçili Aralık"
                value={kpi?.seciliAralikBasvuruSayisi}
                sub={appliedRangeLabel}
                icon={FunnelIcon}
                accentClass="dash-kpi-card--orange"
                isLoading={isFirstLoad && !kpi}
              />
              <KPICard
                label="Ort. Çözüm"
                value={saatFormat(kpi?.seciliAralikOrtalamaCozumSaati)}
                sub="Seçili aralık"
                icon={ClockIcon}
                accentClass="dash-kpi-card--green"
                isLoading={isFirstLoad && !kpi}
              />
              <KPICard
                label="Yıllık Ort."
                value={saatFormat(kpi?.yillikOrtalamaCozumSaati)}
                sub="Bu yılın ort."
                icon={ClockIcon}
                accentClass="dash-kpi-card--teal"
                isLoading={isFirstLoad && !kpi}
              />
            </>
          )}
        </section>
        )}

        {/* FAZ-76 — GÖREV KARTLARI DASHBOARD'DAN KALDIRILDI.
            Kullanıcı (2026-07-30): "dashboard'da görevler barı var ya, bunu
            kaldıralım; dashboard'da görevlerle ilgili bir şey görmek
            istemiyorum." Görevin yeri Görev Ata / Görevlerim sayfaları ve
            sidebar rozetidir; gösterge paneli BAŞVURU panosudur.
            Kart anahtarları (gorev_ozet / gorev_uzerimde) havuzdan da düştü —
            bkz. uiProfiles.js. */}

        <section className="dash-main-grid">
          <div className="dash-main-top-grid" style={mainTopGridStyle}>
            {kartGorunur("harita") && (isFirstLoad && !harita ? (
              <CardSkeleton height={mapHeight} />
            ) : (
              <div
                className={`dash-card dash-card--map${
                  yukleniyor && harita ? " dash-updating" : ""
                }`}
                style={mapCardStyle}
              >
                <div className="dash-card-header">
                  <div className="dash-card-header-left">
                    <div className="dash-card-title">Başvuru Haritası</div>
                    <div className="dash-card-subtitle">
                      {mapPointCount > 0
                        ? `${sayiFormat(mapPointCount)} başvuru konumu`
                        : appliedRangeLabel}
                    </div>
                  </div>

                  <IconExpandButton
                    onClick={() => setMapFullscreen(true)}
                    title="Haritayı tam ekran aç"
                  />
                </div>

                {mapPointCount > 0 ? (
                  <DashboardLeafletMap
                    noktalar={harita?.noktalar || []}
                    durumOzeti={harita?.durumOzeti || []}
                    height={mapHeight}
                  />
                ) : (
                  <div
                    className="dash-empty-lite"
                    style={{
                      height: mapHeight,
                      flexDirection: "column",
                      gap: 6,
                      textAlign: "center",
                      padding: "0 16px",
                    }}
                  >
                    <InboxIcon style={{ width: 22, height: 22 }} />
                    <span>Seçili tarih aralığında konumlu başvuru yok.</span>
                    <span style={{ fontSize: 12, opacity: 0.7 }}>
                      Tarih aralığını genişleterek daha fazla sonuç görebilirsiniz.
                    </span>
                  </div>
                )}
              </div>
            ))}

            <div className="dash-right-column">
              <div className="dash-right-top-grid">
                {kartGorunur("durum_dagilimi") && (isFirstLoad && !grafikler ? (
                  <CardSkeleton height={186} />
                ) : (
                  <DashCard
                    title="Durum Dağılımı"
                    subtitle={`${sayiFormat(
                      (grafikler?.durumDagilimi || []).reduce(
                        (a, x) => a + Number(x?.sayi || 0),
                        0
                      )
                    )} başvuru`}
                    isLoading={yukleniyor && !!grafikler}
                    className="dash-card--status"
                  >
                    <InlineBarChart
                      rows={durumRows}
                      labelKey="etiket"
                      valueKey="sayi"
                      secondaryRenderer={(row) =>
                        row?.oran != null ? yuzdeFormat(row.oran) : null
                      }
                      limit={5}
                    />
                  </DashCard>
                ))}

                {kartGorunur("acik_kapali") && (isFirstLoad && !grafikler ? (
                  <CardSkeleton height={186} />
                ) : (
                  <DashCard
                    title="Açık / Kapalı"
                    subtitle={appliedRangeLabel}
                    isLoading={yukleniyor && !!grafikler}
                    className="dash-card--donut"
                  >
                    <DonutChart
                      acik={acikKapali?.acik || 0}
                      kapali={acikKapali?.kapali || 0}
                    />
                  </DashCard>
                ))}
              </div>

              {kartGorunur("acik_sure") && (isFirstLoad && !grafikler ? (
                <CardSkeleton height={190} />
              ) : (
                <DashCard
                  title="Açık Başvuru Süreleri"
                  subtitle="Bekleme dağılımı"
                  onExpand={() => openExpand("open_age_distribution")}
                  isLoading={yukleniyor && !!grafikler}
                  className="dash-card--age"
                  bodyClassName="dash-card-body--compact-list"
                  expandIconOnly
                  expandTitle="Açık iş yaşı grafiğini büyüt"
                >
                  <InlineBarChart
                    rows={ageChartRows}
                    labelKey="etiket"
                    valueKey="sayi"
                    color="var(--color-warning)"
                    limit={5}
                  />
                </DashCard>
              ))}
            </div>
          </div>

          <div className="dash-main-bottom-grid" style={bottomGridStyle}>
            {kartGorunur("kirilim") && (isFirstLoad && !kirilimlar ? (
              <CardSkeleton height={190} />
            ) : (
              <BreakdownTabbedCard
                mahalleRows={mahalleRows}
                birimRows={birimRows}
                onExpandNeighborhood={() => openExpand("neighborhood_breakdown")}
                onExpandUnit={() => openExpand("unit_breakdown")}
                isLoading={yukleniyor && !!kirilimlar}
                isBaskan={isBaskan}
                style={{ height: "100%" }}
              />
            ))}

            {kartGorunur("kuyruk") && (isFirstLoad && !sonYeni ? (
              <CardSkeleton height={190} />
            ) : (
              <QueueCard
                newRows={sonYeni || []}
                waitingRows={cevapsiz || []}
                onExpandNew={() => openExpand("latest_new")}
                onExpandWaiting={() => openExpand("waiting_new")}
                style={{ height: "100%" }}
              />
            ))}
          </div>
        </section>
      </div>

      <DashboardExpandPanel
        open={Boolean(expandAcikWidget)}
        widgetKey={expandAcikWidget}
        title={WIDGET_TITLES[expandAcikWidget] || "Detay"}
        subtitle={meta?.seciliAralikBasligi}
        summaryData={summaryForWidget?.[expandAcikWidget] || null}
        detailEnvelope={curEnvelope}
        loading={curLoading}
        error={curError}
        onClose={closeExpand}
        onRetry={retryExpand}
        onPrint={() => window.print()}
      />

      {/* Tam ekran harita — üstteki başlık + filtre barı görünür kalır */}
      {mapFullscreen && (
        <div className="dash-map-fs" role="region" aria-label="Başvuru Haritası — Tam ekran">
          <header className="dash-map-fs-header">
            <div className="dash-map-fs-titlewrap">
              <div className="dash-map-fs-title">Başvuru Haritası</div>
              <div className="dash-map-fs-sub">
                {mapPointCount > 0
                  ? `${sayiFormat(mapPointCount)} başvuru konumu`
                  : appliedRangeLabel}
              </div>
            </div>
            <button
              type="button"
              className="dash-map-fs-close"
              onClick={() => setMapFullscreen(false)}
              title="Kapat (Esc)"
              aria-label="Kapat"
            >
              <XMarkIcon />
            </button>
          </header>

          <div className="dash-map-fs-body">
            {mapPointCount > 0 ? (
              <DashboardLeafletMap
                noktalar={harita?.noktalar || []}
                durumOzeti={harita?.durumOzeti || []}
                height="100%"
              />
            ) : (
              <div
                className="dash-empty-lite"
                style={{
                  height: "100%",
                  flexDirection: "column",
                  gap: 6,
                  textAlign: "center",
                  padding: "0 16px",
                }}
              >
                <InboxIcon style={{ width: 22, height: 22 }} />
                <span>Seçili tarih aralığında konumlu başvuru yok.</span>
                <span style={{ fontSize: 12, opacity: 0.7 }}>
                  Tarih aralığını genişleterek daha fazla sonuç görebilirsiniz.
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}